import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Modal, Form, Stack, Image } from "react-bootstrap";
import { useSelector } from 'react-redux';
import Topbar from '../Assets/Topbar';
import LeftSidebar from '../Assets/LeftSidebar';
import { InfinitySpin } from 'react-loader-spinner';
import { postAllShopList, postShopCreate, postShopUpdate, getShopDetailsById, deleteShop, postShopInactivate, getAllState, getAllBarberShop, postRequestOwnership } from '../../../Service';
import { HtmlTagValidation, BlockSQLInjection, checkDuplicateShop, DUPLICATE_SHOP_MESSAGE, DUPLICATE_SHOP_HELPER_MESSAGE, buildShopCreatePayload, buildShopUpdatePayload } from '../../Reusable';
import Select from 'react-select';
import deleteIcon from '../../../images/delete.png';
import editIcon from '../../../images/edit.png';
import reasonInfoIcon from '../../../images/reason-info.png';
import Swal from 'sweetalert2';
import PaginationDesign from '../Assets/Pagination';
// Not loaded globally anywhere (App.js only pulls in App.css/Responsive.css) - without this,
// the .status-badge/.rejected-btn/.request-ownership-btn rules below silently do nothing,
// which is why the Status column badges were rendering at inconsistent widths.
import '../../../css/AdminStyle.css';

// react-select's dropdown popup (menu/options) is styled by its own auto-generated CSS
// classes with hashed names that change between versions - the app's existing dark-theme
// CSS only reaches the input box, not this popup, which is why it renders white. Using the
// "styles" prop instead targets the real parts by name, so it survives library upgrades.
const darkSelectStyles = {
    // Covers the input box itself (typed/focused state was still showing white - the older
    // CSS-based fix for "control" wasn't reaching every state of it, e.g. when focused).
    // transparent (not a solid dark color) to exactly match the other fields around it
    // (.custom-modal .form-control uses "background: transparent" over the modal's own
    // background) - a solid color here looked visibly different from its neighbors.
    control: (base) => ({ ...base, backgroundColor: 'transparent', borderColor: '#565656' }),
    menu: (base) => ({ ...base, backgroundColor: '#1c1c1c', border: '1px solid #565656' }),
    menuList: (base) => ({ ...base, backgroundColor: '#1c1c1c' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#D49D29' : state.isFocused ? '#333' : '#1c1c1c',
        color: state.isSelected ? '#000' : '#fff',
        cursor: 'pointer'
    }),
    singleValue: (base) => ({ ...base, color: '#fff' }),
    input: (base) => ({ ...base, color: '#fff' }),
    placeholder: (base) => ({ ...base, color: '#9a9a9a' })
};

export default function StylistBusinessList() {

    // get stylist's own name/phone, used to auto-fill manager details when they are not the owner
    const stylistUser = useSelector(store => store.user['stylistuser']); 

    const [shopList, setShopList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [listCount, setListCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [submitLoader, setSubmitLoader] = useState(false);
    const [stateProvinceOption, setStateProvinceOption] = useState([]);

    // ---- Requirement 6: Professional-Initiated Ownership Request ----
    // The shop list response already carries status/requestedBy/requestedDate per business
    // (Admin's BusinessList relies on the same postAllShopList fields), so "Requested" is
    // read straight from the backend instead of guessed locally - status: 1 = Pending,
    // 2 = Approved, 3 = Rejected, but status alone isn't reliable (it defaults to 1 even for
    // businesses nobody ever requested), so it's combined with requestedBy matching this
    // professional to mean "I have a genuine, still-unresolved request on this shop". Backend
    // added approvedBy/approvedDate, set once Admin approves a request - but status stays stuck
    // at 1 even then (same unreliability as above), so !shop.approvedBy is needed too or an
    // already-approved request would keep showing "Requested" forever.
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestTargetShop, setRequestTargetShop] = useState(null);
    const [requestReason, setRequestReason] = useState('');
    const [requestReasonError, setRequestReasonError] = useState('');
    const [requestSubmitLoader, setRequestSubmitLoader] = useState(false);
    const isRequestPendingForMe = (shop) => shop.status === 1 && Boolean(shop.requestedBy) && String(shop.requestedBy) === String(stylistUser?.clientId) && !shop.approvedBy;

    const initialFormValues = {
        createdBy: stylistUser?.clientId || 0,
        name: '',
        address1: '',
        address2: '',
        city: '',
        zipCode: '',
        phoneNumber: '',
        isOwnership: '',
        managerFirstName: '',
        managerLastName: '',
        managerPhoneNumber: '',
        active: true,
        countryId: 1,
        stateProvinceId: ''
    };
    const [formValues, setFormValues] = useState(initialFormValues);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchShopList(currentPage, perPage);
        getAllState().then(res => {
            if (res.data.statusCode === 200) {
                const options = res.data.stateList.map(s => ({ label: s.stateName, value: s.stateId }));
                setStateProvinceOption(options);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage]);

    const fetchShopList = (page, size) => {
        setLoader(true);
        // searchText is now a required field on this API (backend added it) - this page has
        // no search UI, so always sent empty; ownedByFilter/statusFilter default to "All" (0),
        // since the Requested By/Date/Reason data those drive is Admin-only per backend.
        postAllShopList({ pageNumber: page, pageSize: size, searchText: '', ownedByFilter: 0, statusFilter: 0 }).then(res => {
            if (res.data.statusCode === 200) {
                setShopList(res.data.shopList);
                setListCount(res.data.filteredCount);
            }
            setLoader(false);
        }).catch(() => setLoader(false));
    };

    const getBackCurrentPage = (page, size) => {
        setCurrentPage(page);
        setPerPage(size);
    };

    // ---- Requirement 6: Professional-Initiated Ownership Request ----
    // shop.ownedBy comes back as a string ("262") from the API; stylistUser.clientId is a
    // number, so this compares them as strings to avoid a type-mismatch false negative.
    // Split into 3 distinct cases (rather than the earlier 2) so an unassigned business the
    // professional created keeps Edit/Delete AND gets a Request Ownership control alongside,
    // instead of one replacing the other.
    const isShopOwnedByMe = (shop) => Boolean(shop.ownedBy) && String(shop.ownedBy) === String(stylistUser?.clientId);
    const isShopUnassigned = (shop) => !shop.ownedBy;
    // The remaining case (owned by someone else) doesn't need its own check function - it's
    // simply whatever's left once isShopOwnedByMe and isShopUnassigned are both false.

    // Confirmed flow: a shop I created (Owner = No) stays fully mine to Edit/Delete while
    // it's unassigned - no need to "request" what I already made. Only once Admin later gives
    // it to someone else does it become something I'd need to fight to get back, so it shows
    // Request Ownership alongside Edit/Delete at that point too. Backend now sends "createdBy"
    // per shop in the list response, so this is live (confirmed against a real response).
    const isCreatedByMe = (shop) => Boolean(shop.createdBy) && String(shop.createdBy) === String(stylistUser?.clientId);

    // ---- Frontend-only workaround: once Admin reassigns a shop to a different professional,
    // the shop's own status moves away from "Rejected" (it's now "Owned") - the backend only
    // keeps one current status on the shop itself, not "professional X was rejected for shop
    // Y" specifically, so that history is gone server-side the moment it's reassigned. That
    // let a previously-rejected professional see "Request Ownership" again for the same shop.
    // This remembers it locally (per professional, via localStorage) the moment this
    // professional's own screen ever sees the shop as Rejected+unassigned, so it stays hidden
    // for them afterward - even across reloads, even once it's reassigned to someone else.
    // Caveat: only works if this professional actually viewed the shop while it still showed
    // Rejected (before Admin reassigned it) - it can't retroactively know about one it missed.
    // Real fix still needs the backend to keep proper per-professional rejection history.
    const rejectedShopsStorageKey = `rejectedShopIds_${stylistUser?.clientId || 'guest'}`;
    const getRejectedShopIds = () => {
        try {
            return JSON.parse(localStorage.getItem(rejectedShopsStorageKey)) || [];
        } catch {
            return [];
        }
    };
    // shop.status === 3 is checked directly first - it already tells us this shop was
    // rejected (per backend's STATUS_MAP: 1 Pending, 2 Approved, 3 Rejected), so an owned
    // shop with status 3 (e.g. shop 206) shows Rejected immediately instead of waiting on
    // localStorage to have caught it earlier while it happened to be unassigned. The
    // localStorage check stays as a fallback for the case the status comment above describes
    // (Admin reassigning the shop can move status away from 3), so a professional who already
    // saw it as Rejected doesn't lose that once it's given to someone else.
    const isRejectedForMe = (shop) => shop.status === 3 || getRejectedShopIds().includes(shop.id);
    const markRejectedForMe = (shopId) => {
        const current = getRejectedShopIds();
        if (!current.includes(shopId)) {
            try {
                localStorage.setItem(rejectedShopsStorageKey, JSON.stringify([...current, shopId]));
            } catch {
                // localStorage unavailable (private mode etc.) - silently skip, falls back to
                // the old behavior of only blocking while shop.status stays 3.
            }
        }
    };
    useEffect(() => {
        shopList.forEach(shop => {
            if (isShopUnassigned(shop) && shop.status === 3) {
                markRejectedForMe(shop.id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopList]);

    // getStateLabel prefers shop.state (the real field the backend already sends), falling
    // back to the stateProvinceOption lookup only if it's absent.
    const getStateLabel = (shop) => shop.state || (stateProvinceOption.find(s => s.value === shop.stateProvinceId) || {}).label || '';

    // Dedicated "Owner" column - "Owned by You" / "Owned by <name>" / "Unassigned".
    // Kept separate from the Action column below, which only shows buttons/links.
    const getOwnerText = (shop) => {
        if (!shop.ownedBy) return 'Unassigned';
        if (String(shop.ownedBy) === String(stylistUser?.clientId)) return 'Owned by You';
        // ownerByName is the new field backend added specifically for display - ownedBy stays
        // the id (used above for the comparison), managerFirstName/Last are still null.
        return shop.ownerByName ? `Owned by ${shop.ownerByName}` : 'Owned by another professional';
    };

    // "Requested By" / "Reason" columns. Admin's BusinessList resolves requestedBy (a
    // professional id) to a name via getProfessionalList - but that endpoint is Admin/Super-Admin
    // only, so calling it from this professional-facing page sent a request with no admin token,
    // which the backend correctly 401'd and tripped the app's global "session expired" handler,
    // logging the professional out even though their own session was fine. Backend now sends
    // requestedByName directly on the shop (same pattern as the existing ownerByName), so this
    // reads that instead of needing any extra lookup call.
    const getRequestedByName = (shop) => shop.requestedByName || (shop.requestedBy ? `#${shop.requestedBy}` : '-');
    // Same clickable info-icon + popup pattern as Admin's Reason column, instead of raw text
    // inline, so both pages read consistently and the column stays compact.
    const showRequestReason = (shop) => {
        Swal.fire({ icon: 'info', title: 'Reason', text: shop.request_Reason || 'No reason provided', confirmButtonText: 'Close' });
    };
    const getReasonCell = (shop) => shop.request_Reason
        ? <img src={reasonInfoIcon} alt="View reason" title="View reason" className="reason-info-icon" onClick={() => showRequestReason(shop)} />
        : '-';

    const handleOpenRequest = (shop) => {
        setRequestTargetShop(shop);
        setRequestReason('');
        setRequestReasonError('');
        setShowRequestModal(true);
    };

    // ---- Dedicated Status column ----
    // Separate from the Action column (which only holds Edit/Delete now): this tells you
    // where the shop stands. Mirrors the Action column's own priority order rather than a
    // flat "has an owner -> Approved" check - a shop I created that Admin later gave away is
    // still mine to reclaim, so it shows Rejected/Requested/Request Ownership same as any
    // unassigned shop, NOT "Approved" just because it happens to have an owner now. "Approved"
    // only means "this one's settled and isn't something I have (or can start) a claim on".
    // .status-badge (AdminStyle.css) still sizes the "Request Ownership" button below so the
    // column stays aligned; the three passive labels use .status-text instead (icon + plain
    // colored text, no icon, no background chip): green for Approved, red for Rejected,
    // blue for Requested. Same colors as Admin's BusinessList so the two pages read
    // consistently.
    // Rejected only: small clickable info icon next to the pill that pops up the reason
    // Admin gave (shop.reject_Reason) - Stylist's own "Reason" column shows the professional's
    // *request* reason (request_Reason), not this, so without this icon there's nowhere to
    // see why a request was rejected.
    const showRejectReason = (shop) => {
        Swal.fire({ icon: 'info', title: 'Rejection Reason', text: shop.reject_Reason || 'No reason provided', confirmButtonText: 'Close' });
    };

    const renderStatusLabel = (type, label, shop) => (
        <span className="status-cell-wrap">
            <span className={`status-text status-text-${type}`}>{label}</span>
            {type === 'rejected' && (
                <img
                    src={reasonInfoIcon}
                    alt="View reason"
                    title="View reason"
                    className="reason-info-icon"
                    onClick={() => showRejectReason(shop)}
                />
            )}
        </span>
    );

    const getStatusCell = (shop) => {
        if (isShopOwnedByMe(shop)) {
            // "Approved" should only ever mean Admin actually approved a request. status 0
            // means "nothing's been approved yet" - covers both self-owned-at-creation
            // (isOwnership = Y, no request ever made) AND a shop Admin just handed me via
            // Assign Owner that I haven't accepted - ownedBy is already set in both cases,
            // but status stays 0 until something real happens. Checked directly on status
            // (not isCreatedByMe, which only caught the first case and missed the second -
            // same fix already applied to Admin's BusinessList).
            if (shop.status === 0) {
                return '-';
            }
            return renderStatusLabel('approved', 'Approved');
        }
        if (isShopUnassigned(shop) && isCreatedByMe(shop)) {
            // My own creation, still unassigned - nothing to request, nothing to show.
            return '-';
        }
        if (isRejectedForMe(shop)) {
            return renderStatusLabel('rejected', 'Rejected', shop);
        }
        if (isRequestPendingForMe(shop)) {
            return renderStatusLabel('requested', 'Requested');
        }
        if (!isShopUnassigned(shop) && !isCreatedByMe(shop)) {
            // Owned by someone else and it's not mine to reclaim - purely informational.
            return renderStatusLabel('approved', 'Approved');
        }
        // Outline button (same idea as Admin's Assign Owner/Reassign, which use
        // outline-warning/outline-primary too) - blue border/text only, no fill, until you
        // actually hover it (hand cursor), where Bootstrap's own outline-button behavior
        // inverts it to a solid blue fill.
        return <Button size="sm" variant="outline-primary" className="status-badge request-ownership-btn" onClick={() => handleOpenRequest(shop)}>Request Ownership</Button>;
    };

    const handleRequestSubmit = () => {
        if (!requestReason || requestReason.trim() === '') {
            setRequestReasonError('Reason for request is required');
            return;
        }
        setRequestSubmitLoader(true);
        postRequestOwnership({ shopId: requestTargetShop.id, reason: requestReason.trim() }).then(res => {
            setRequestSubmitLoader(false);
            if (res.data.statusCode === 200) {
                setShowRequestModal(false);
                // Re-fetch so the row's status/requestedBy/requestedDate come from the backend
                // (the real source of truth) rather than being approximated locally.
                fetchShopList(currentPage, perPage);
                Swal.fire({ position: 'center', icon: 'success', title: res.data.statusMessage || 'Ownership requested successfully!', showConfirmButton: false, timer: 3000 });
            } else {
                // Just show the backend's error - status/Requested/Rejected always come from
                // shop.status via the next fetch, never guessed from the error message text.
                Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.statusMessage });
            }
        }).catch(() => setRequestSubmitLoader(false));
    };

    // ---- Add ----
    const handleAddOpen = () => {
        setFormValues(initialFormValues);
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        const errors = validate(formValues);
        setFormErrors(errors);
        if (Object.keys(errors).length === 0) {
            const allShopsRes = await getAllBarberShop();
            if (allShopsRes.data.statusCode === 200 && checkDuplicateShop(allShopsRes.data.shopList, formValues)) {
                setFormErrors(prev => ({ ...prev, name: DUPLICATE_SHOP_MESSAGE, stateProvinceId: DUPLICATE_SHOP_HELPER_MESSAGE }));
                return;
            }
            setSubmitLoader(true);
            postShopCreate(buildShopCreatePayload(formValues)).then(res => {
                if (res.data.statusCode === 200) {
                    setShowAddModal(false);
                    setSubmitLoader(false);
                    fetchShopList(currentPage, perPage);
                    Swal.fire({ position: 'center', icon: 'success', title: 'Shop added successfully!', showConfirmButton: false, timer: 3000 });
                } else if (res.data.statusCode === 400) {
                    // Backend's own duplicate-business check (Name + Address1 + State) - same rule as
                    // our client-side pre-check above, kept as a safety net. Uses our own wording
                    // (not res.data.statusMessage) so both layers show identical text to the user.
                    setSubmitLoader(false);
                    setFormErrors(prev => ({ ...prev, name: DUPLICATE_SHOP_MESSAGE, stateProvinceId: DUPLICATE_SHOP_HELPER_MESSAGE }));
                } else {
                    setSubmitLoader(false);
                    Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.statusMessage });
                }
            }).catch(() => setSubmitLoader(false));
        }
    };

    // ---- Edit ----
    const handleEditOpen = (shopId) => {
        getShopDetailsById(shopId).then(res => {
            if (res.data.statusCode === 200) {
                const shop = res.data.shopList[0];
                setFormValues({
                    shopId: shop.id,
                    createdBy: shop.createdBy || stylistUser?.clientId || 0,
                    name: shop.name || '',
                    address1: shop.address1 || '',
                    address2: shop.address2 || '',
                    city: shop.city || '',
                    zipCode: shop.zipPostalCode || '',
                    phoneNumber: shop.phoneNumber || '',
                    isOwnership: shop.isOwnership || '',
                    managerFirstName: shop.managerFirstName || '',
                    managerLastName: shop.managerLastName || '',
                    managerPhoneNumber: shop.managerPhoneNumber || '',
                    active: shop.active,
                    countryId: 1,
                    stateProvinceId: shop.stateProvinceId || ''
                });
                setFormErrors({});
                setShowEditModal(true);
            }
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        const errors = validate(formValues);
        setFormErrors(errors);
        if (Object.keys(errors).length === 0) {
            const allShopsRes = await getAllBarberShop();
            if (allShopsRes.data.statusCode === 200 && checkDuplicateShop(allShopsRes.data.shopList, formValues, formValues.shopId)) {
                setFormErrors(prev => ({ ...prev, name: DUPLICATE_SHOP_MESSAGE, stateProvinceId: DUPLICATE_SHOP_HELPER_MESSAGE }));
                return;
            }
            setSubmitLoader(true);
            postShopUpdate(buildShopUpdatePayload(formValues)).then(res => {
                if (res.data.statusCode === 200) {
                    setShowEditModal(false);
                    setSubmitLoader(false);
                    fetchShopList(currentPage, perPage);
                    Swal.fire({ position: 'center', icon: 'success', title: 'Shop updated successfully!', showConfirmButton: false, timer: 3000 });
                } else {
                    setSubmitLoader(false);
                    Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.statusMessage });
                }
            }).catch(() => setSubmitLoader(false));
        }
    };

    // ---- Delete ----
    const handleDelete = (shopId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this shop?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteShop(shopId).then(res => {
                    if (res.data.statusCode === 200) {
                        Swal.fire({ position: 'center', icon: 'success', title: 'Shop deleted successfully!', showConfirmButton: false, timer: 3000 });
                        fetchShopList(currentPage, perPage);
                    } else {
                        Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.statusMessage });
                    }
                });
            }
        });
    };

    // ---- Active / Inactive Toggle ----
    const handleToggleActive = (shop) => {
        const newStatus = !shop.active;
        const actionText = newStatus ? 'Activate' : 'Inactivate';
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to ${actionText} this shop?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Yes, ${actionText} it!`
        }).then((result) => {
            if (result.isConfirmed) {
                postShopInactivate({ shopId: shop.id, active: newStatus }).then(res => {
                    if (res.data.statusCode === 200) {
                        setShopList(prev => prev.map(s =>
                            s.id === shop.id ? { ...s, active: newStatus } : s
                        ));
                        Swal.fire({
                            position: 'center',
                            icon: 'success',
                            title: newStatus ? 'Shop activated successfully!' : 'Shop inactivated successfully!',
                            showConfirmButton: false,
                            timer: 3000
                        });
                    } else {
                        Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.statusMessage });
                    }
                }).catch(() => {
                    Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong. Please try again.' });
                });
            }
        });
    };

    // ---- Phone helpers ----
    const formatPhoneDigits = (digits) => {
        if (!digits || digits.length === 0) return '';
        let fmt = '(' + digits.slice(0, 3);
        if (digits.length > 3) fmt += ') ' + digits.slice(3, 6);
        if (digits.length > 6) fmt += '-' + digits.slice(6, 10);
        return fmt;
    };

    const handlePhoneKeyDown = (e) => {
        BlockSQLInjection(e);
        if (e.key === 'Backspace' && ['phoneNumber', 'managerPhoneNumber'].includes(e.target.name)) {
            e.preventDefault();
            const digits = (formValues[e.target.name] || '').replace(/\D/g, '');
            setFormValues(prev => ({ ...prev, [e.target.name]: formatPhoneDigits(digits.slice(0, -1)) }));
        }
    };

    // ---- Form Change ----
    const handleFormChange = (e) => {
        const { name, value } = e.target;

        if (name === 'isOwnership') {
            // Owner = Yes: capture the professional's own details as the manager, instead of asking for them
            setFormValues(prev => ({
                ...prev,
                isOwnership: value,
                managerFirstName: value === 'Y' ? (stylistUser?.firstName || '') : '',
                managerLastName: value === 'Y' ? (stylistUser?.lastName || '') : '',
                managerPhoneNumber: value === 'Y' ? (stylistUser?.phoneNumber || '') : ''
            }));
            return;
        }

        let filteredValue = value;

        if (['managerFirstName', 'managerLastName', 'city'].includes(name)) {
            filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
        } else if (['phoneNumber', 'managerPhoneNumber'].includes(name)) {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            filteredValue = formatPhoneDigits(digits);
        } else if (name === 'zipCode') {
            filteredValue = value.replace(/[^0-9]/g, '').slice(0, 5);
        }

        const capitalizedFields = ['name', 'address1', 'address2', 'city', 'managerFirstName', 'managerLastName'];
        const finalValue = capitalizedFields.includes(name) && filteredValue.length > 0
            ? filteredValue.charAt(0).toUpperCase() + filteredValue.slice(1)
            : filteredValue;
        setFormValues(prev => ({ ...prev, [name]: finalValue }));
    };

    // ---- Validation ----
    const validate = (values) => {
        const errors = {};

        if (!values.name || values.name.trim() === '') {
            errors.name = 'Business name is required';
        } else if (!HtmlTagValidation(values.name)) {
            errors.name = 'Invalid input';
        }

        if (!values.address1 || values.address1.trim() === '') {
            errors.address1 = 'Address 1 is required';
        } else if (!HtmlTagValidation(values.address1)) {
            errors.address1 = 'Invalid input';
        }

        if (!values.city || values.city.trim() === '') {
            errors.city = 'City is required';
        } else if (!/^[a-zA-Z\s]+$/.test(values.city.trim())) {
            errors.city = 'City should contain letters only';
        } else if (!HtmlTagValidation(values.city)) {
            errors.city = 'Invalid input';
        }

        if (!values.stateProvinceId) {
            errors.stateProvinceId = 'Please select a state';
        }

        if (!values.zipCode || values.zipCode.trim() === '') {
            errors.zipCode = 'Zipcode is required';
        } else if (values.zipCode.length !== 5) {
            errors.zipCode = 'Zipcode must be 5 digits';
        }

        if (!values.phoneNumber || values.phoneNumber.trim() === '') {
            errors.phoneNumber = 'Phone number is required';
        } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(values.phoneNumber.trim())) {
            errors.phoneNumber = 'Phone number must be 10 digits';
        }

        if (!values.isOwnership || values.isOwnership === '') {
            errors.isOwnership = 'Please select ownership';
        }

        if (values.isOwnership === 'Y') {
            if (!values.managerFirstName || values.managerFirstName.trim() === '') {
                errors.managerFirstName = 'First name is required';
            } else if (!/^[a-zA-Z\s]+$/.test(values.managerFirstName.trim())) {
                errors.managerFirstName = 'First name should contain letters only';
            } else if (!HtmlTagValidation(values.managerFirstName)) {
                errors.managerFirstName = 'Invalid input';
            }
            if (!values.managerLastName || values.managerLastName.trim() === '') {
                errors.managerLastName = 'Last name is required';
            } else if (!/^[a-zA-Z\s]+$/.test(values.managerLastName.trim())) {
                errors.managerLastName = 'Last name should contain letters only';
            } else if (!HtmlTagValidation(values.managerLastName)) {
                errors.managerLastName = 'Invalid input';
            }
            if (!values.managerPhoneNumber || values.managerPhoneNumber.trim() === '') {
                errors.managerPhoneNumber = 'Phone number is required';
            } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(values.managerPhoneNumber.trim())) {
                errors.managerPhoneNumber = 'Phone number must be 10 digits';
            }
        }

        return errors;
    };

    // ---- Shared Modal Form Body ----
    const renderFormFields = () => (
        <>
            <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Business Name <span className='error'>*</span></Form.Label>
                    <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="name"
                        value={formValues.name}
                        onChange={handleFormChange}
                        placeholder="Enter business name"
                    />
                    {formErrors.name && <span className='error'>{formErrors.name}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Are you the <span style={{whiteSpace:'nowrap', color:'inherit'}}>owner? <span className='error'>*</span></span></Form.Label>
                    <Form.Select name="isOwnership" value={formValues.isOwnership} onChange={handleFormChange}>
                        <option value="">-- Select --</option>
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                    </Form.Select>
                    {formErrors.isOwnership && <span className='error'>{formErrors.isOwnership}</span>}
                </Form.Group>
            </Row>

            {formValues.isOwnership === 'Y' && (
                <>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mb-2">
                            <Form.Label>Owner First Name <span className='error'>*</span></Form.Label>
                            <Form.Control
                                onKeyDown={BlockSQLInjection}
                                type="text"
                                name="managerFirstName"
                                value={formValues.managerFirstName}
                                onChange={handleFormChange}
                                placeholder="Enter first name"
                            />
                            {formErrors.managerFirstName && <span className='error'>{formErrors.managerFirstName}</span>}
                        </Form.Group>
                        <Form.Group as={Col} className="mb-2">
                            <Form.Label>Owner Last Name <span className='error'>*</span></Form.Label>
                            <Form.Control
                                onKeyDown={BlockSQLInjection}
                                type="text"
                                name="managerLastName"
                                value={formValues.managerLastName}
                                onChange={handleFormChange}
                                placeholder="Enter last name"
                            />
                            {formErrors.managerLastName && <span className='error'>{formErrors.managerLastName}</span>}
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mb-2">
                            <Form.Label>Owner Phone Number <span className='error'>*</span></Form.Label>
                            <Form.Control
                                onKeyDown={handlePhoneKeyDown}
                                type="text"
                                name="managerPhoneNumber"
                                value={formValues.managerPhoneNumber}
                                onChange={handleFormChange}
                                placeholder="(XXX) XXX-XXXX"
                                maxLength={14}
                            />
                            {formErrors.managerPhoneNumber && <span className='error'>{formErrors.managerPhoneNumber}</span>}
                        </Form.Group>
                        <Col></Col>
                    </Row>
                </>
            )}

            <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Address 1 <span className='error'>*</span></Form.Label>
                    <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="address1"
                        value={formValues.address1}
                        onChange={handleFormChange}
                        placeholder="Enter address 1"
                    />
                    {formErrors.address1 && <span className='error'>{formErrors.address1}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Address 2</Form.Label>
                    <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="address2"
                        value={formValues.address2}
                        onChange={handleFormChange}
                        placeholder="Enter address 2"
                    />
                </Form.Group>
            </Row>

            <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>City <span className='error'>*</span></Form.Label>
                    <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="city"
                        value={formValues.city}
                        onChange={handleFormChange}
                        placeholder="Enter city"
                    />
                    {formErrors.city && <span className='error'>{formErrors.city}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2 select-search-drp adm">
                    <Form.Label>State <span className='error'>*</span></Form.Label>
                    <Select
                        className='selectSearch'
                        styles={darkSelectStyles}
                        options={stateProvinceOption}
                        name='stateProvinceId'
                        onChange={(selected) => setFormValues(prev => ({ ...prev, stateProvinceId: selected ? selected.value : '' }))}
                        value={stateProvinceOption.find(s => s.value === formValues.stateProvinceId) || null}
                        placeholder="Select state"
                    />
                    {formErrors.stateProvinceId && <span className='error'>{formErrors.stateProvinceId}</span>}
                </Form.Group>
            </Row>

            <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Zipcode <span className='error'>*</span></Form.Label>
                    <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="zipCode"
                        value={formValues.zipCode}
                        onChange={handleFormChange}
                        placeholder="Enter zipcode"
                        maxLength={5}
                    />
                    {formErrors.zipCode && <span className='error'>{formErrors.zipCode}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                    <Form.Label>Phone Number <span className='error'>*</span></Form.Label>
                    <Form.Control
                        onKeyDown={handlePhoneKeyDown}
                        type="text"
                        name="phoneNumber"
                        value={formValues.phoneNumber}
                        onChange={handleFormChange}
                        placeholder="(XXX) XXX-XXXX"
                        maxLength={14}
                    />
                    {formErrors.phoneNumber && <span className='error'>{formErrors.phoneNumber}</span>}
                </Form.Group>
            </Row>
        </>
    );

    return (
        <section className='dashboard'>
            <Container>
                <Row className='sideBarRow'>
                    <Col md={3} className='sideBarCol'>
                        <LeftSidebar />
                    </Col>
                    <Col md={9}>
                        <div className='rightsidebar'>

                            <Topbar />

                            <Row className="mb-3 main-title">
                                <Col>
                                    <h1>Business List</h1>
                                </Col>
                            </Row>

                            <Row className="mb-3 managesearch">
                                <Col>
                                    <Stack direction="horizontal" gap={2}>
                                        <div className="add-btn ms-auto">
                                            <Button variant="primary" onClick={handleAddOpen}>Add Shop</Button>
                                        </div>
                                    </Stack>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <div className="table-responsive" id="no-more-tables">
                                        <Table striped bordered hover variant="dark" className='emailwrap'>
                                            <thead>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th style={{ minWidth: '160px' }}>Business Shop</th>
                                                    <th>Address</th>
                                                    <th>State</th>
                                                    <th style={{ minWidth: '150px' }}>Owner</th>
                                                    <th style={{ minWidth: '110px' }}>Requested By</th>
                                                    <th style={{ minWidth: '130px', whiteSpace: 'nowrap' }}>Requested Date</th>
                                                    <th style={{ minWidth: '70px' }}>Requested Reason</th>
                                                    <th style={{ minWidth: '150px' }}>Status</th>
                                                    <th>Active/Inactive</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    loader === true ?
                                                        <tr>
                                                            <td colSpan='11' style={{ textAlign: 'center', paddingLeft: '0px' }}>
                                                                <InfinitySpin height="80" width="200" radius="9" color="#D49D29" ariaLabel="loading" wrapperStyle wrapperClass />
                                                            </td>
                                                        </tr>
                                                    :
                                                        shopList.length > 0 ?
                                                            shopList.map((shop, index) => (
                                                                <tr key={shop.id}>
                                                                    <td className='table-row-number'>{index + 1 + (currentPage * perPage) - perPage}</td>
                                                                    <td data-title="Business Shop">{shop.name}</td>
                                                                    <td data-title="Address">{shop.address1 || '-'}</td>
                                                                    <td data-title="State">{getStateLabel(shop) || '-'}</td>
                                                                    <td data-title="Owner">{getOwnerText(shop)}</td>
                                                                    <td data-title="Requested By">{getRequestedByName(shop)}</td>
                                                                    <td data-title="Requested Date">{shop.requestedDate || '-'}</td>
                                                                    <td data-title="Reason">{getReasonCell(shop)}</td>
                                                                    <td data-title="Status">{getStatusCell(shop)}</td>
                                                                    <td data-title="Active/Inactive">
                                                                        <Form.Check
                                                                            type="switch"
                                                                            id={`switch-${shop.id}`}
                                                                            checked={shop.active === true}
                                                                            // Same access rule as the Edit/Delete branch above: the confirmed
                                                                            // owner, or the professional who created it while it's still
                                                                            // unassigned - not once Admin has given it to someone else.
                                                                            disabled={!(isShopOwnedByMe(shop) || (isShopUnassigned(shop) && isCreatedByMe(shop)))}
                                                                            onChange={() => handleToggleActive(shop)}
                                                                            label={shop.active ? 'Active' : 'Inactive'}
                                                                        />
                                                                    </td>
                                                                    <td data-title="Action" className='professionalAction'>
                                                                        {
                                                                            // Edit/Delete only - Approved/Rejected/Requested/Request Ownership
                                                                            // now live in their own Status column. Available when I'm the
                                                                            // confirmed owner, or I created it and it's still unassigned.
                                                                            isShopOwnedByMe(shop) || (isShopUnassigned(shop) && isCreatedByMe(shop)) ?
                                                                                <Stack direction="horizontal" gap={3} className='professionAction'>
                                                                                    <div>
                                                                                        <Image src={editIcon} style={{ cursor: 'pointer' }} onClick={() => handleEditOpen(shop.id)} className='tableActionIcon' />
                                                                                        <Button className='actionText dltBtn' style={{ cursor: 'pointer' }} onClick={() => handleEditOpen(shop.id)}>Edit</Button>
                                                                                    </div>
                                                                                    <div>
                                                                                        <Image src={deleteIcon} style={{ cursor: 'pointer' }} onClick={() => handleDelete(shop.id)} className='tableActionIcon' />
                                                                                        <Button className='actionText dltBtn' style={{ cursor: 'pointer' }} onClick={() => handleDelete(shop.id)}>Delete</Button>
                                                                                    </div>
                                                                                </Stack>
                                                                            :
                                                                                '-'
                                                                        }
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        :
                                                            <tr>
                                                                <td colSpan='11' style={{ textAlign: 'center' }} className='noDataFound'>No Data Found...</td>
                                                            </tr>
                                                }
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>

                            {
                                listCount > 0 ?
                                    <PaginationDesign currentPage={currentPage} listCount={listCount} perPage={perPage} getBack={getBackCurrentPage} />
                                :
                                    ''
                            }

                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} className="custom-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Shop</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSubmit}>
                    <Modal.Body>
                        {renderFormFields()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={submitLoader}>
                            {submitLoader ? 'Saving...' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} className="custom-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Shop</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        {renderFormFields()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={submitLoader}>
                            {submitLoader ? 'Updating...' : 'Update'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Request Ownership Modal - Requirement 6 */}
            <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} className="custom-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Request Ownership</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Business</Form.Label>
                        <Form.Control
                            type="text"
                            readOnly
                            value={requestTargetShop ? [requestTargetShop.name, requestTargetShop.address1, getStateLabel(requestTargetShop)].filter(Boolean).join(', ') : ''}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Reason for request <span className='error'>*</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            onKeyDown={BlockSQLInjection}
                            value={requestReason}
                            onChange={(e) => {
                                const v = e.target.value;
                                setRequestReason(v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v);
                                setRequestReasonError('');
                            }}
                            placeholder="Explain why you're requesting ownership of this business"
                        />
                        {requestReasonError && <span className='error'>{requestReasonError}</span>}
                    </Form.Group>
                    <p style={{ fontSize: '0.85rem', color: '#9a9a9a', marginTop: '10px' }}>
                        Sent to Admin along with this request. The request date is recorded automatically.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRequestModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleRequestSubmit} disabled={requestSubmitLoader}>
                        {requestSubmitLoader ? 'Submitting...' : 'Submit Request'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </section>
    );
}
